import { getDefaultBackend } from './PhotoStore'
import { StorageAdapter } from '../storage/StorageAdapter'
import { base64ToBlob, decodedByteLength } from './base64'
import { dbgWarn } from '../logger'

/**
 * photos/resolveBlob.js — el resolveBlob REAL que reemplaza el stub de F3.
 *
 * El builder PURO (occurrences-builder.js:38,83,116) llama `resolveBlob(record) => Blob|null` para
 * adjuntar el binario de foto al multipart. En F3 el handler pasaba un stub que devolvia siempre
 * null (datos+ref sincronizaban, binario diferido a F5). Aqui se construye el real: lee el binario
 * del backend activo (web: por image_ref; nativo: por local_path) y lo convierte a Blob.
 *
 * INYECCION (Pitfall 3): esto se inyecta en el HANDLER (modules/occurrences.js / tasks.js), NUNCA
 * en el builder — el builder sigue PURO (PUSH-05). El backend es inyectable para los gates.
 */


// ── Integridad (v9): la vara es photo_meta.byte_length, sellada en capture() ─────────────────────

// byte_length esperado del binario: del record si el caller ya lo trae (listPending* puede
// seleccionarlo), si no lookup por PK (barato, indexado). NULL/ausente = fila LEGACY pre-v9 ->
// SIN verificación (comportamiento pre-fix: jamás marcar missing una foto buena vieja).
async function expectedBytes (record, db) {
  if (record.byte_length !== undefined && record.byte_length !== null) return record.byte_length
  if (record.byte_length === null) return null
  try {
    const r = await db.query('SELECT byte_length FROM photo_meta WHERE image_ref=?', [record.image_ref])
    const row = (r && r.values && r.values[0]) || null
    if (!row) return null
    return row.byte_length ?? (Array.isArray(row) ? row[0] : null) ?? null
  } catch {
    return null // sin verificación posible -> pass-through (no bloquear una foto por un fallo de lectura)
  }
}

// Saca el binario corrupto del pipeline. Best-effort e idempotente, NUNCA lanza: flipa photo_meta a
// 'missing' (solo desde 'pending' — jamás resucitar un 'done') para que (a) el overlay deje de
// decodificar basura en cada reingreso, (b) unconfirmedRefs del drain lo cuente como pérdida y dispare
// la nota photo_missing (la maquinaria del caso 0-bytes, que ya existía), (c) la purga lo barra.
async function markCorrupt (imageRef, db) {
  try {
    await db.run("UPDATE photo_meta SET upload_status='missing' WHERE image_ref=? AND upload_status='pending'", [imageRef], true)
  } catch { /* best-effort */ }
}

/**
 * Crea el resolveBlob ligado a un backend.
 * @param {{ backend?: object }} deps
 * @returns {(record: object) => Promise<Blob|null>}
 */
export function makeResolveBlob ({ backend = getDefaultBackend(), db = StorageAdapter } = {}) {
  return async (record) => {
    if (!record || !record.image_ref) return null
    const base64 = await backend.read(record)
    if (base64 == null) return null
    const blob = await base64ToBlob(base64)
    // Guard pérdida-silenciosa: un blob de 0 bytes = binario corrupto/ilegible (base64ToBlob NUNCA
    // lanza: ante datos no decodificables devuelve un Blob VACÍO truthy). Tratarlo como NO-resuelto
    // (null) para que el builder lo OMITA (no sobrescriba la foto ya subida) y caiga en
    // reportMissingPhotos, en vez de subir un `file` de 0 bytes que pisaría la imagen server-side.
    if (!(blob && blob.size > 0)) return null
    // Integridad (v9): un binario TRUNCADO (>0 bytes pero < lo sellado en capture) pasaba el guard de
    // arriba, se SUBÍA al server tal cual y quedaba blanco para siempre (reproducido: truncado al 30%
    // -> multipart con el JPEG mutilado -> op done). Mismatch = tratarlo como ausente.
    const exp = await expectedBytes(record, db)
    if (exp != null && blob.size !== exp) {
      dbgWarn('photo', `binario TRUNCADO ref=${record.image_ref}: ${blob.size}/${exp} bytes -> omitido (no se sube)`)
      await markCorrupt(record.image_ref, db)
      return null
    }
    return blob
  }
}

/**
 * Crea un resolver que devuelve el base64 (string) crudo del backend, sin pasar por Blob/FileReader.
 * Lo usa el handler de evidencia (picture/file espera un string base64, no un Blob). Funciona en
 * node (gates) y en device. Devuelve null si no hay binario recuperable.
 * @param {{ backend?: object }} deps
 * @returns {(record: object) => Promise<string|null>}
 */
export function makeResolveBase64 ({ backend = getDefaultBackend(), db = StorageAdapter } = {}) {
  return async (record) => {
    if (!record || !record.image_ref) return null
    const base64 = await backend.read(record)
    if (base64 == null) return null
    // Integridad (v9): el overlay/editor consumen este resolver. Un base64 truncado dispara `load`
    // (no error) con las dimensiones completas y pinta las primeras filas + BLANCO — por eso la
    // validación va AQUÍ, antes del <img>. Mismatch -> null: el slot vuelve a cámara virgen (re-toma)
    // en vez de pintar blanco mudo.
    const exp = await expectedBytes(record, db)
    if (exp != null && decodedByteLength(base64) !== exp) {
      dbgWarn('photo', `binario TRUNCADO ref=${record.image_ref} (overlay/editor) -> omitido (no se pinta)`)
      await markCorrupt(record.image_ref, db)
      return null
    }
    return base64
  }
}
